import { useLanguage } from "../hooks/useLanguage";
import translationsData from "../translations/about.json";
import type { Translations } from "../translations/types";

const translations = translationsData as Translations;

export function About() {
  const lang = useLanguage();
  const t = translations[lang];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header with Profile Picture */}
      <div className="bg-card rounded-lg border shadow-lg p-8">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Profile Picture */}
          <div className="flex-shrink-0">
            <img 
              src="https://images.gizemon.wine/instagram/3677990978309147903_1.jpg"
              alt="Profile"
              className="w-48 h-48 rounded-full object-cover"
              width="192"
              height="192"
            />
          </div>

          {/* Intro */}
          <div className="flex-1 space-y-4">
            <h1 className="text-4xl font-bold">{t.aboutMe.title}</h1>
            
            <div className="prose prose-lg dark:prose-invert max-w-none space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                {t.aboutMe.intro}
              </p>

              <p className="text-muted-foreground leading-relaxed">
                {t.aboutMe.note}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Extended Bio Section */}
      <div className="bg-card rounded-lg border shadow-lg p-8">
        <h2 className="text-2xl font-bold mb-4">{t.background.title}</h2>
        <div className="prose dark:prose-invert max-w-none space-y-4">
          <p className="text-muted-foreground leading-relaxed">
            {t.background.para1}
          </p>
          
          <p className="text-muted-foreground leading-relaxed">
            {t.background.para2}
          </p>

          <p className="text-muted-foreground leading-relaxed">
            {t.background.para3}
          </p>

          <p 
            className="text-muted-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: t.background.para4 }}
          />

          <p 
            className="text-muted-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: t.background.para5 }}
          />
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-card rounded-lg border shadow-lg p-8">
        <h2 className="text-2xl font-bold mb-6">{t.getInTouch.title}</h2>
        
        <div className="space-y-6">
          {/* Social Links */}
          <div>
            <h3 className="text-lg font-semibold mb-3">{t.getInTouch.followMe}</h3>
            <div className="flex flex-col gap-3">
              <a 
                href="https://instagram.com/gizemonwine" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="group-hover:underline">{t.getInTouch.instagram}</span>
              </a>
              
              <a 
                href="mailto:green.brain2280@fastmail.com" 
                className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="group-hover:underline">{t.getInTouch.email}</span>
              </a>
            </div>
          </div>

          {/* Note */}
          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              {t.getInTouch.note}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

